import { CurrencyInr, Paperclip, Plus, Trash } from "@phosphor-icons/react"
import {
  Button,
  FormInput,
  FormQuerySelector,
  Group,
  InputGroup,
  Tabs,
  Tag,
  Text,
  Toast,
} from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import { cloneDeep } from "lodash-es"
import { useEffect, useRef, useState } from "react"
import AttachmentModal from "~/components/AttachmentModal"
import { FormCard } from "~/components/FormCard"
import {
  GstBreakdownCard,
  type GstAttachment,
} from "~/components/GstBreakdownCard"
import DetailTable from "~/components/ProductionPlanning/DetailTable"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import {
  useAllCommoditiesQuery,
  useAllPlotCropsQuery,
  useAllSiUnitsQuery,
  useAllVarietiesQuery,
  useAllVarietyGradesQuery,
} from "~/generated/graphql"
import {
  createEmptyTradeCommodityBlock,
  getCommodityParticularsTotals,
  syncNestedTradeToFormValues,
  type TradeCommodityBlock,
} from "~/utils/tradeCommodityItems"

type TradeParticularsProps = {
  form: any
  qcForm: any
  inputProps: (name: string, sharedEntity?: { group: SharedEntityGroup }) => any
  runComputations: (values: any) => void
  defaultCurrency?: { name?: string }
  getRemainingQty: (item?: any) => number
  removedTradeItems: Record<string, any>
  setRemovedTradeItems: React.Dispatch<
    React.SetStateAction<Record<string, any>>
  >
  finalTotal: number
  showDeviationInDraftWarning: boolean
  total_returned?: number | string | null
  bookingSummaryId?: number | string | null
  offerId?: string | null
  createSauda?: boolean
  isStatusGenerateSale: boolean
  isStatusInvoiced: boolean
  isCompletelyDisabled: boolean
  isHishobPatti: boolean
  isBookingTrade: boolean
  isParentBookingTrade: boolean
  isParentSaudaPavti?: boolean
  isOrderPatti: boolean
  newStoragePatti: boolean
  inwardFromDemand: boolean
  newHishobPatti: boolean
  offerOrderHasOffers: boolean
  isTradeConfirmed: boolean
  disableAddCommodityGrade: boolean
  allowMultipleCommodity: boolean
  supplierSameState?: boolean
  buyerSameState?: boolean
  deleteSellerGstAttachments?: (vars: any) => Promise<any>
  deleteBuyerGstAttachments?: (vars: any) => Promise<any>
  disableAttachments?: boolean
}

function getFlatGradeIndex(
  blocks: TradeCommodityBlock[],
  cIdx: number,
  gIdx: number
) {
  let index = 0
  for (let i = 0; i < cIdx; i++) {
    index += blocks[i]?.grades?.length || 0
  }
  return index + gIdx
}

function TradeCommodityPanel({
  cIdx,
  blocks,
  state,
  form,
  qcForm,
  inputProps,
  defaultCurrency,
  getRemainingQty,
  removedTradeItems,
  gradeDisabled,
  commodityBlockDisabled,
  isStatusGenerateSale,
  isStatusInvoiced,
  isCompletelyDisabled,
  isHishobPatti,
  isBookingTrade,
  isParentBookingTrade,
  isParentSaudaPavti,
  bookingSummaryId,
  offerId,
  createSauda,
  onAddGrade,
  onRemoveGrade,
  onRemoveCommodity,
  runComputations,
  supplierSameState,
  buyerSameState,
}: {
  cIdx: number
  blocks: TradeCommodityBlock[]
  state: any
  form: any
  qcForm: any
  inputProps: TradeParticularsProps["inputProps"]
  defaultCurrency?: { name?: string }
  getRemainingQty: (item?: any) => number
  removedTradeItems: Record<string, any>
  gradeDisabled: boolean
  commodityBlockDisabled: boolean
  isStatusGenerateSale: boolean
  isStatusInvoiced: boolean
  isCompletelyDisabled: boolean
  isHishobPatti: boolean
  isBookingTrade: boolean
  isParentBookingTrade: boolean
  isParentSaudaPavti?: boolean
  bookingSummaryId?: number | string | null
  offerId?: string | null
  createSauda?: boolean
  onAddGrade: () => void
  onRemoveGrade: (gIdx: number) => void
  onRemoveCommodity?: () => void
  runComputations: (values: any) => void
  supplierSameState?: boolean
  buyerSameState?: boolean
  deleteSellerGstAttachments?: (vars: any) => Promise<any>
  deleteBuyerGstAttachments?: (vars: any) => Promise<any>
}) {
  const commodityBlock = blocks?.[cIdx]
  const grades = commodityBlock.grades?.length
    ? commodityBlock.grades
    : [{ rate: 0 }]
  const isChildTrade = Boolean(state?.parent_trade?.id)

  const commodityTotals = getCommodityParticularsTotals(
    commodityBlock,
    qcForm.values?.results
  )

  const gstBaseRef = useRef<any>(null)
  useEffect(() => {
    gstBaseRef.current = null
  }, [form.values])

  // const unitEditable = (gIdx: number) => gIdx === 0

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormQuerySelector
          label="Commodity"
          placeholder="Select"
          required
          serverSideQuery
          dataHook={useAllCommoditiesQuery}
          {...inputProps(`trade_commodity_items.${cIdx}.commodity`, {
            group: SharedEntityGroup.KiteCommodity,
          })}
          disabled={commodityBlockDisabled}
        />
        <FormQuerySelector
          label="Variety"
          placeholder="Select"
          required
          dataHook={useAllVarietiesQuery}
          variables={{
            commodity_id: state.trade_commodity_items?.[cIdx]?.commodity?.id,
          }}
          {...inputProps(`trade_commodity_items.${cIdx}.commodity_variety`, {
            group: SharedEntityGroup.KiteVariety,
          })}
          disabled={commodityBlockDisabled}
        />
        <div className="flex gap-1">
          <FormQuerySelector
            label="Plot Selection"
            placeholder="Select"
            dataHook={useAllPlotCropsQuery}
            variables={{
              farmer: state?.seller?.id,
              crop: state.trade_commodity_items?.[cIdx]?.commodity?.crop_id,
            }}
            getOptionLabel={({ tag, name, external_reference_id, id }: any) => {
              if (!id) return undefined
              return `${tag} ${name} | ${external_reference_id}`
            }}
            {...inputProps(`trade_commodity_items.${cIdx}.plot_crop`, {
              group: SharedEntityGroup.KitePlotCrop,
            })}
            disabled={commodityBlockDisabled || !state?.seller?.id}
            className={"!w-full"}
          />
          {onRemoveCommodity ? (
            <div className="flex justify-end">
              <Button
                variant="minimal"
                intent="danger"
                size="sm"
                leftIcon={<Trash className="!mr-0" />}
                onClick={onRemoveCommodity}
                className="!self-end mb-1"
              />
            </div>
          ) : null}
        </div>
      </div>

      <DetailTable
        className={classNames(
          state?.is_intercompany_transaction === "yes"
            ? "grid-cols-5"
            : "grid-cols-4"
        )}
        headers={[
          {
            label: "Grade",
            field: "variety_grade",
            required: true,
            render: (gIdx: number) => {
              const grade = grades[gIdx]
              const itemRemoved = grade?.id
                ? Boolean(removedTradeItems[grade.id])
                : false

              return (
                <FormQuerySelector
                  placeholder="Select"
                  dataHook={useAllVarietyGradesQuery}
                  variables={{
                    variety:
                      state.trade_commodity_items?.[cIdx]?.commodity_variety
                        ?.id,
                  }}
                  {...inputProps(
                    `trade_commodity_items.${cIdx}.grades.${gIdx}.variety_grade`,
                    { group: SharedEntityGroup.KiteGrade }
                  )}
                  isDisabled={gradeDisabled || itemRemoved}
                  isOptionDisabled={(opt: any) =>
                    grades.some(
                      (g, i) => i !== gIdx && g.variety_grade?.id === opt.id
                    )
                  }
                  className={"w-full"}
                />
              )
            },
          },
          state?.is_intercompany_transaction === "yes"
            ? {
                label: "Batch No.",
                field: "batch_number",
                required: !isChildTrade,
                render: (gIdx: number) => {
                  const grade = grades[gIdx]
                  const batchLocked =
                    isChildTrade && Boolean(grade?.batch_number)
                  return (
                    <div className="flex flex-col gap-2 w-full">
                      <FormInput
                        placeholder="Batch#"
                        {...inputProps(
                          `trade_commodity_items.${cIdx}.grades.${gIdx}.batch_number`
                        )}
                        disabled={
                          state?.is_intercompany_transaction !== "yes" ||
                          batchLocked ||
                          isStatusGenerateSale ||
                          isStatusInvoiced ||
                          (isCompletelyDisabled && !isHishobPatti)
                        }
                      />
                    </div>
                  )
                },
              }
            : undefined,
          {
            label: "Rate",
            field: "rate",
            required: true,
            hideInTotal: true,
            render: (gIdx: number) => {
              const grade = grades[gIdx]
              const qcResult = qcForm.values?.results?.find(
                (result: any) =>
                  result.template.grade.name === grade.variety_grade?.name
              )
              const baseRateKey: "daily_rate" | "rate" = qcResult
                ? "daily_rate"
                : "rate"
              const dailyRate = Number(grade.daily_rate) || 0
              const evalutation = qcResult?.evaluations?.[0]
              const recommendedDeducatedRate =
                dailyRate - dailyRate * ((evalutation?.value || 0) / 100)
              const itemRemoved = grade?.id
                ? Boolean(removedTradeItems[grade.id])
                : false

              return (
                <div className="flex flex-col gap-2 w-full">
                  <FormInput
                    placeholder="Enter"
                    {...inputProps(
                      `trade_commodity_items.${cIdx}.grades.${gIdx}.${baseRateKey}`
                    )}
                    disabled={
                      isStatusGenerateSale ||
                      isStatusInvoiced ||
                      (isCompletelyDisabled && !isHishobPatti) ||
                      itemRemoved
                    }
                    leftElement={
                      <Tag className="ml-1" variant="minimal">
                        {defaultCurrency?.name || "INR"}
                      </Tag>
                    }
                  />
                  {qcResult ? (
                    <FormInput
                      placeholder="Enter"
                      {...inputProps(
                        `trade_commodity_items.${cIdx}.grades.${gIdx}.rate`
                      )}
                      disabled={
                        isStatusGenerateSale ||
                        isStatusInvoiced ||
                        (isCompletelyDisabled && !isHishobPatti) ||
                        itemRemoved
                      }
                      type="number"
                      leftElement={
                        <Tag variant="minimal">
                          {defaultCurrency?.name || "INR"}
                        </Tag>
                      }
                      helperText={`Recommended QC Deducted Rate : ${inr(
                        recommendedDeducatedRate
                      )}`}
                    />
                  ) : null}
                </div>
              )
            },
          },
          {
            label: "Quantity",
            field: "quantity",
            required: true,
            showTotal: true,
            render: (gIdx: number) => {
              const grade = grades[gIdx]
              const flatIndex = getFlatGradeIndex(blocks, cIdx, gIdx)
              const flatItem = state.trade_items?.[flatIndex] ?? grade
              const remainingQuantity = getRemainingQty(flatItem)
              const itemRemoved = grade?.id
                ? Boolean(removedTradeItems[grade.id])
                : false

              return (
                <FormInput
                  placeholder="Enter"
                  {...inputProps(
                    `trade_commodity_items.${cIdx}.grades.${gIdx}.quantity`
                  )}
                  disabled={
                    isStatusGenerateSale ||
                    isStatusInvoiced ||
                    (isCompletelyDisabled && !isHishobPatti) ||
                    itemRemoved
                  }
                  inputProps={{
                    ...inputProps(
                      `trade_commodity_items.${cIdx}.grades.${gIdx}.quantity`
                    ),
                    id:
                      cIdx === 0 && gIdx === 0
                        ? "vt_weight_listener"
                        : undefined,
                    type: "number",
                    className: "mr-24",
                    onChange: (e: any) => {
                      const value = Number(e.target.value)
                      const original = Number(remainingQuantity)
                      const result =
                        (bookingSummaryId || offerId || createSauda) &&
                        value > original
                          ? original
                          : e.target.value
                      inputProps(
                        `trade_commodity_items.${cIdx}.grades.${gIdx}.quantity`
                      ).onChange(result)
                    },
                  }}
                  type="number"
                  rightElement={
                    <FormQuerySelector
                      components={{
                        IndicatorSeparator: () => null,
                        ClearIndicator: () => null,
                      }}
                      getControlStyles={(prev: any) => ({
                        ...prev,
                        border: "none",
                      })}
                      dataHook={useAllSiUnitsQuery}
                      variables={{ type: ["Weight", "SKU"] }}
                      isDisabled={
                        isStatusGenerateSale ||
                        isStatusInvoiced ||
                        (isCompletelyDisabled && !isHishobPatti) ||
                        itemRemoved
                        //  ||
                        // !unitEditable(gIdx)
                      }
                      {...inputProps(
                        `trade_commodity_items.${cIdx}.quantity_unit`,
                        {
                          group: SharedEntityGroup.KiteSiUnit,
                        }
                      )}
                    />
                  }
                />
              )
            },
          },
          {
            label: "Total Cost",
            field: "total_cost",
            showTotal: true,
            render: (gIdx: number) => {
              const grade = grades[gIdx]
              const flatIndex = getFlatGradeIndex(blocks, cIdx, gIdx)
              const flatItem = state.trade_items?.[flatIndex] ?? grade
              const qcResult = qcForm.values?.results?.find(
                (result: any) =>
                  result.template.grade.name === grade.variety_grade?.name
              )
              const baseRateKey: "daily_rate" | "rate" = qcResult
                ? "daily_rate"
                : "rate"
              const dailyRate = Number(grade.daily_rate) || 0
              const evalutation = qcResult?.evaluations?.[0]
              const recommendedDeducatedRate =
                dailyRate - dailyRate * ((evalutation?.value || 0) / 100)

              return (
                <div className="flex flex-col gap-2 w-full">
                  <FormInput
                    placeholder="0"
                    leftElement={<CurrencyInr />}
                    value={inr(
                      (Number(grade.quantity) || 0) *
                        (Number(grade[baseRateKey]) || 0)
                    )}
                    disabled
                    className="!w-full"
                  />
                  {qcResult ? (
                    <FormInput
                      value={inr(flatItem.total_amount)}
                      disabled
                      helperText={`Amount after system QC deduc. : ${inr(
                        recommendedDeducatedRate * (Number(grade.quantity) || 0)
                      )}`}
                    />
                  ) : null}
                </div>
              )
            },
          },
        ]?.filter(a => a)}
        rows={grades.map((g: any) => {
          const qcResult = qcForm.values?.results?.find(
            (result: any) =>
              result.template.grade.name === g.variety_grade?.name
          )
          const baseRateKey: "daily_rate" | "rate" = qcResult
            ? "daily_rate"
            : "rate"
          return {
            ...g,
            total_cost:
              (Number(g.quantity) || 0) * (Number(g[baseRateKey]) || 0),
          }
        })}
        addRowLabel="Grade"
        onAddRow={onAddGrade}
        onRemoveRow={onRemoveGrade}
        namePrefix={`trade_commodity_items.${cIdx}.grades`}
        inputProps={inputProps}
        addDisable={
          gradeDisabled ||
          isChildTrade ||
          // isBookingTrade ||
          isStatusGenerateSale ||
          isStatusInvoiced ||
          isParentBookingTrade ||
          isParentSaudaPavti
        }
        deleteDisable={
          gradeDisabled || isStatusGenerateSale || isStatusInvoiced
        }
        showTotals
        totals={{
          quantity: commodityTotals.quantity,
          total_cost: commodityTotals.total_cost,
        }}
      />

      {(() => {
        const gst = (commodityBlock as any).gst || {
          supplier_sgst: 0,
          supplier_cgst: 0,
          supplier_igst: 0,
          supplier_sgst_percentage: 0,
          supplier_cgst_percentage: 0,
          supplier_igst_percentage: 0,
          supplier_gst_auto_calculated: true,
          seller_gst_attachments: [],
          buyer_sgst: 0,
          buyer_cgst: 0,
          buyer_igst: 0,
          buyer_sgst_percentage: 0,
          buyer_cgst_percentage: 0,
          buyer_igst_percentage: 0,
          buyer_gst_auto_calculated: true,
          buyer_gst_attachments: [],
          _gstFromBackend: false,
        }
        const totalSupplierGst =
          (gst.supplier_igst || 0) +
          (gst.supplier_sgst || 0) +
          (gst.supplier_cgst || 0)
        const totalBuyerGst =
          (gst.buyer_igst || 0) + (gst.buyer_sgst || 0) + (gst.buyer_cgst || 0)
        const setGstField = (field: string, value: any) => {
          form.setFieldValue(
            `trade_commodity_items.${cIdx}.gst.${field}`,
            value
          )
          const updatedValues = cloneDeep(gstBaseRef.current ?? form.values)
          const blk = updatedValues.trade_commodity_items?.[cIdx]
          if (blk?.gst) blk.gst[field] = value
          gstBaseRef.current = updatedValues
          runComputations(updatedValues)
        }
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <GstBreakdownCard
              label="Supplier"
              autoCalculated={gst.supplier_gst_auto_calculated}
              onAutoChange={val => {
                setGstField("supplier_gst_auto_calculated", val)
                setGstField("_gstFromBackend", false)
              }}
              isDisabled={gradeDisabled}
              isSameState={supplierSameState}
              totalGst={totalSupplierGst}
              igst={gst.supplier_igst || 0}
              sgst={gst.supplier_sgst || 0}
              cgst={gst.supplier_cgst || 0}
              igstPct={gst.supplier_igst_percentage}
              sgstPct={gst.supplier_sgst_percentage}
              cgstPct={gst.supplier_cgst_percentage}
              igstField="supplier_igst_percentage"
              sgstField="supplier_sgst_percentage"
              cgstField="supplier_cgst_percentage"
              onPctChange={(field, pct) => {
                setGstField(field, pct)
              }}
              attachments={gst.seller_gst_attachments || []}
              onAddAttachment={(newOnes: GstAttachment[]) => {
                setGstField("seller_gst_attachments", [
                  ...(gst.seller_gst_attachments || []),
                  ...newOnes,
                ])
              }}
              onRemoveAttachment={(index: number) => {
                const arr = [...(gst.seller_gst_attachments || [])]
                arr.splice(index, 1)
                setGstField("seller_gst_attachments", arr)
              }}
            />
            <GstBreakdownCard
              label="Buyer"
              autoCalculated={gst.buyer_gst_auto_calculated}
              onAutoChange={val => {
                setGstField("buyer_gst_auto_calculated", val)
                setGstField("_gstFromBackend", false)
              }}
              isDisabled={gradeDisabled}
              isSameState={buyerSameState}
              totalGst={totalBuyerGst}
              igst={gst.buyer_igst || 0}
              sgst={gst.buyer_sgst || 0}
              cgst={gst.buyer_cgst || 0}
              igstPct={gst.buyer_igst_percentage}
              sgstPct={gst.buyer_sgst_percentage}
              cgstPct={gst.buyer_cgst_percentage}
              igstField="buyer_igst_percentage"
              sgstField="buyer_sgst_percentage"
              cgstField="buyer_cgst_percentage"
              onPctChange={(field, pct) => {
                setGstField(field, pct)
              }}
              attachments={gst.buyer_gst_attachments || []}
              onAddAttachment={(newOnes: GstAttachment[]) => {
                setGstField("buyer_gst_attachments", [
                  ...(gst.buyer_gst_attachments || []),
                  ...newOnes,
                ])
              }}
              onRemoveAttachment={(index: number) => {
                const arr = [...(gst.buyer_gst_attachments || [])]
                arr.splice(index, 1)
                setGstField("buyer_gst_attachments", arr)
              }}
            />
          </div>
        )
      })()}

      <div className="grid grid-cols-4 gap-4 text-base text-gray-800 p-3 bg-gray-50 rounded border border-dashed border-gray-200">
        <div className="col-span-2 font-medium">
          Total:{" "}
          <span className=" text-gray-600 ">
            {" "}
            {`${commodityBlock?.commodity_variety?.name || ""}`}{" "}
          </span>
          <span className="italic text-gray-500 text-sm"> (Excl. Tax)</span>
        </div>
        <div>
          {commodityTotals.quantity}{" "}
          {commodityBlock?.quantity_unit?.name ||
            state?.quantity_unit?.name ||
            "na"}
          {commodityTotals.total_quantity_kg
            ? ` (${commodityTotals.total_quantity_kg} kg)`
            : ""}
        </div>
        <div className="text-right font-medium">
          {(() => {
            const gst = (commodityBlock as any).gst
            const supplierGst =
              (gst?.supplier_igst || 0) +
              (gst?.supplier_sgst || 0) +
              (gst?.supplier_cgst || 0)
            const buyerGst =
              (gst?.buyer_igst || 0) +
              (gst?.buyer_sgst || 0) +
              (gst?.buyer_cgst || 0)
            return inr(commodityTotals.total_cost)
          })()}
        </div>
      </div>
    </div>
  )
}

export const TradeParticulars = ({
  form,
  qcForm,
  inputProps,
  runComputations,
  defaultCurrency,
  getRemainingQty,
  removedTradeItems,
  finalTotal,
  showDeviationInDraftWarning,
  total_returned,
  bookingSummaryId,
  offerId,
  createSauda,
  isStatusGenerateSale,
  isStatusInvoiced,
  isCompletelyDisabled,
  isHishobPatti,
  isBookingTrade,
  isParentBookingTrade,
  isParentSaudaPavti,
  isOrderPatti,
  newStoragePatti,
  inwardFromDemand,
  newHishobPatti,
  offerOrderHasOffers,
  isTradeConfirmed,
  disableAddCommodityGrade,
  allowMultipleCommodity,
  supplierSameState,
  buyerSameState,
  deleteSellerGstAttachments,
  deleteBuyerGstAttachments,
  disableAttachments,
}: TradeParticularsProps) => {
  const { values: state, setValues } = form
  const isChildTrade = Boolean(state?.parent_trade?.id)
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false)
  const [activeTabIdx, setActiveTabIdx] = useState(0)

  const attachments: any[] = state?.attachments || []

  const blocks: TradeCommodityBlock[] =
    state.trade_commodity_items?.length > 0
      ? state.trade_commodity_items
      : [createEmptyTradeCommodityBlock()]

  const applyState = (stateCopy: any) => {
    const synced = syncNestedTradeToFormValues(stateCopy)
    setValues(synced)
    runComputations(synced)
  }

  const addCommodity = () => {
    const stateCopy = cloneDeep(state)
    if (!stateCopy.trade_commodity_items) stateCopy.trade_commodity_items = []
    const ref = stateCopy.trade_commodity_items?.[0]
    stateCopy.trade_commodity_items.push(
      createEmptyTradeCommodityBlock({
        quantity_unit: stateCopy.quantity_unit ?? ref?.quantity_unit,
      })
    )
    setActiveTabIdx(stateCopy.trade_commodity_items.length - 1)
    applyState(stateCopy)
  }

  const removeCommodity = (cIdx: number) => {
    const stateCopy = cloneDeep(state)
    stateCopy.trade_commodity_items.splice(cIdx, 1)
    if (!stateCopy.trade_commodity_items.length) {
      stateCopy.trade_commodity_items = [createEmptyTradeCommodityBlock()]
      setActiveTabIdx(0)
    } else {
      setActiveTabIdx(Math.max(0, cIdx - 1))
    }
    applyState(stateCopy)
  }

  const addGrade = (cIdx: number) => {
    const stateCopy = cloneDeep(state)
    const block = stateCopy.trade_commodity_items[cIdx]
    if (!block.grades) block.grades = []
    const unit =
      block.quantity_unit ??
      block.grades?.[0]?.quantity_unit ??
      stateCopy.quantity_unit
    block.grades.unshift({
      quantity: 0,
      variety_grade: null,
      rate: 0,
      rate_unit: unit,
      quantity_unit: unit,
      attachments: [],
    })
    applyState(stateCopy)
  }

  const removeGrade = (cIdx: number, gIdx: number) => {
    const stateCopy = cloneDeep(state)
    const grades = stateCopy.trade_commodity_items[cIdx].grades
    grades.splice(gIdx, 1)
    if (!grades.length) {
      stateCopy.trade_commodity_items[cIdx].grades = [{ rate: 0, quantity: 0 }]
    }
    applyState(stateCopy)
  }

  const gradeDisabled =
    isStatusGenerateSale ||
    isStatusInvoiced ||
    (isCompletelyDisabled && !isHishobPatti) ||
    isOrderPatti ||
    createSauda ||
    newStoragePatti ||
    inwardFromDemand ||
    (newHishobPatti && offerOrderHasOffers)

  const commodityBlockDisabled =
    isStatusGenerateSale ||
    isStatusInvoiced ||
    (isCompletelyDisabled && !isHishobPatti) ||
    isOrderPatti ||
    createSauda ||
    newStoragePatti ||
    inwardFromDemand

  const panelProps = (cIdx: number) => ({
    cIdx,
    blocks,
    state,
    form,
    qcForm,
    inputProps,
    defaultCurrency,
    getRemainingQty,
    removedTradeItems,
    gradeDisabled,
    commodityBlockDisabled,
    isStatusGenerateSale,
    isStatusInvoiced,
    isCompletelyDisabled,
    isHishobPatti,
    isBookingTrade,
    isParentBookingTrade,
    isParentSaudaPavti,
    bookingSummaryId,
    offerId,
    createSauda,
    runComputations,
    supplierSameState,
    buyerSameState,
    deleteSellerGstAttachments,
    deleteBuyerGstAttachments,
    onAddGrade: () => addGrade(cIdx),
    onRemoveGrade: (gIdx: number) => {
      const grade = blocks[cIdx].grades?.[gIdx]
      if (isStatusGenerateSale || isStatusInvoiced) {
        return
      }
      removeGrade(cIdx, gIdx)
    },
    onRemoveCommodity:
      allowMultipleCommodity && blocks.length > 1
        ? () => removeCommodity(cIdx)
        : undefined,
  })

  const tabItems = blocks.map((block, cIdx) => ({
    id: `commodity-${cIdx}`,
    header: block.commodity?.name || `Commodity ${cIdx + 1}`,
    panel: <TradeCommodityPanel {...panelProps(cIdx)} />,
  }))

  return (
    <FormCard
      title="Trade Particulars"
      action={
        <Button
          variant="ghost"
          size="sm"
          intent="info"
          className="flex items-center gap-1"
          onClick={() => setIsAttachmentModalOpen(true)}
          leftIcon={<Paperclip size={15} />}
        >
          {attachments.length}
        </Button>
      }
    >
      <div className="mb-3 border rounded-lg overflow-hidden">
        <header className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 p-3 md:p-4 bg-gradient-to-r from-green-50 to-transparent border-b">
          <h6 className="text-base font-semibold text-gray-800">
            Trade Particulars
          </h6>
          {isChildTrade || disableAddCommodityGrade ? null : (
            <Button
              leftIcon={<Plus />}
              onClick={addCommodity}
              variant="ghost"
              hidden={!allowMultipleCommodity}
            >
              Commodity
            </Button>
          )}
        </header>

        <div className="px-4 pt-3">
          <Toast
            title="Action Restricted"
            subtitle="Avoid modifying rate or quantity when a deviation request is pending to prevent credit errors."
            intent="danger"
            variant="minimal"
            hidden={!showDeviationInDraftWarning}
            className="min-w-full text-xs"
          />
        </div>

        <div className="p-3 md:p-4">
          {allowMultipleCommodity ? (
            <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-200 mb-4">
              <Tabs
                key={`${activeTabIdx}-${blocks.length}`}
                className="flex-1 min-w-0"
                // @ts-expect-error – Headless UI Tab.Group supports defaultIndex
                defaultIndex={activeTabIdx}
                tabs={tabItems}
              />
            </div>
          ) : (
            <TradeCommodityPanel {...panelProps(0)} />
          )}
        </div>

        <div className="border-t-2 border-green-100 bg-gradient-to-b from-gray-50 to-white">
          <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 border-b">
            <Text className="font-semibold text-gray-700 md:flex-[2_1_0%]">
              Total Consolidated Commodities Amount{" "}
              <span className="italic text-gray-500 text-sm"> (Excl. Tax)</span>
            </Text>
            <div className="flex justify-between md:block md:flex-1">
              <span className="text-sm text-gray-500 md:hidden">Quantity:</span>
              <div className="flex flex-col gap-1 self-end">
                <Text className="text-right font-medium">
                  {state.total_quantity}{" "}
                  {allowMultipleCommodity
                    ? "Units"
                    : state?.quantity_unit?.name || "na"}
                </Text>
                {total_returned ? (
                  <Tag
                    className="text-right self-end !rounded-xl"
                    intent="danger"
                    variant="minimal"
                  >
                    {`Revised: ${total_returned} ${
                      state?.quantity_unit?.name || "na"
                    }`}
                  </Tag>
                ) : null}
              </div>
            </div>
            <div className="flex justify-between md:block md:flex-[0.75]">
              <span className="text-sm text-gray-500 md:hidden">Amount:</span>
              <Text className="text-right font-medium">
                {inr(state.total_amount)}
              </Text>
            </div>
            <span className="hidden md:block w-10" />
          </Group>
          <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 border-b bg-blue-50/30">
            <Text className="font-semibold text-gray-700 md:flex-1">
              Standard Deduction
            </Text>
            <Group className="flex gap-2 items-center md:flex-1">
              <InputGroup
                inputProps={{
                  ...inputProps("standard_deduction"),
                  max: 100,
                  min: 0,
                  type: "number",
                  placeholder: "Value in %",
                  className: "text-right",
                }}
                className="flex-1"
                disabled={
                  isStatusGenerateSale ||
                  isStatusInvoiced ||
                  isCompletelyDisabled
                }
                rightElement={<span className="mr-2">%</span>}
              />
            </Group>
            <div className="flex justify-between md:block md:flex-1">
              <span className="text-sm text-gray-500 md:hidden">Quantity:</span>
              <Text className="text-right">
                {state.quantity_deducted?.toFixed?.(2)}{" "}
                {allowMultipleCommodity
                  ? "Units"
                  : state?.quantity_unit?.name || "na"}
              </Text>
            </div>
            <div className="flex justify-between md:block md:flex-[0.75]">
              <span className="text-sm text-gray-500 md:hidden">Amount:</span>
              <Text className="text-right">{inr(state.amount_deducted)}</Text>
            </div>
            <span className="hidden md:block w-10" />
          </Group>
          <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 bg-gradient-to-r from-green-100 to-green-50 border-t-2 border-green-200">
            <Text className="text-lg font-bold text-green-800 md:flex-[2_1_0%]">
              Final Total
              <span className="italic text-gray-500 text-sm"> (Excl. Tax)</span>
            </Text>
            <div className="flex justify-between md:block md:flex-1">
              <span className="text-sm font-medium text-green-700 md:hidden">
                Quantity:
              </span>
              <Text className="text-right font-bold text-green-800">
                {(
                  (form.values.total_quantity || 0) -
                  (form.values.quantity_deducted || 0)
                )?.toFixed(2)}{" "}
                {allowMultipleCommodity
                  ? "Units"
                  : state?.quantity_unit?.name || "na"}
              </Text>
            </div>
            <div className="flex justify-between md:block md:flex-[0.75]">
              <span className="text-sm font-medium text-green-700 md:hidden">
                Amount:
              </span>
              <Text className="text-right font-bold text-green-800 text-lg">
                {inr(finalTotal)}
              </Text>
            </div>
            <span className="hidden md:block w-10" />
          </Group>
        </div>
      </div>

      {isAttachmentModalOpen ? (
        <AttachmentModal
          form={form}
          isOpen={isAttachmentModalOpen}
          attachments={attachments}
          addAttachment={attachments => {
            form.setFieldValue("attachments", attachments)
          }}
          disabled={disableAttachments}
          onClose={() => setIsAttachmentModalOpen(false)}
        />
      ) : null}
    </FormCard>
  )
}
